import * as THREE from 'three';
import { box, cyl, M, mat, mesh, sphere } from '../materials';
import { glowTexture } from '../textures';
import { cooler, signBoard } from './props';

/** Little things neighbors lose (a favor: find them). Each is a few inches across. */
export function lostThing(name: string): THREE.Group {
  const g = new THREE.Group();
  if (name.includes('glasses')) {
    const rim = mat('#2a2014', { rough: 0.4 });
    for (const x of [-0.045, 0.045]) {
      const r = mesh(new THREE.TorusGeometry(0.035, 0.007, 5, 14), rim, x, 0.012, 0);
      r.rotation.x = Math.PI / 2;
      g.add(r);
    }
    g.add(box(0.03, 0.008, 0.008, rim, 0, 0.012, 0));
  } else if (name.includes('bass')) {
    g.add(box(0.16, 0.035, 0.05, mat('#4f7a3a', { rough: 0.5 }), 0, 0.02, 0));
    g.add(box(0.05, 0.035, 0.08, mat('#3d5e2c'), -0.1, 0.02, 0).rotateY(0.4));
  } else if (name.includes('oil')) {
    g.add(cyl(0.025, 0.03, 0.09, mat('#7d4fb8', { rough: 0.2 }), 0, 0.045, 0, 10));
    g.add(cyl(0.012, 0.012, 0.03, M.black, 0, 0.1, 0, 8));
  } else if (name.includes('lighter')) {
    g.add(box(0.03, 0.07, 0.015, mat('#d6452f', { rough: 0.3 }), 0, 0.035, 0));
  } else if (name.includes('dentures')) {
    const gum = mesh(new THREE.TorusGeometry(0.04, 0.014, 6, 12, Math.PI), mat('#e48a9a'), 0, 0.012, 0);
    gum.rotation.x = Math.PI / 2;
    g.add(gum);
    const teeth = mesh(new THREE.TorusGeometry(0.04, 0.011, 6, 12, Math.PI), mat('#f4f1e6'), 0, 0.026, 0);
    teeth.rotation.x = Math.PI / 2;
    g.add(teeth);
  } else if (name.includes('phone')) {
    g.add(box(0.075, 0.012, 0.15, mat('#16181c', { rough: 0.2 }), 0, 0.006, 0));
    g.add(
      box(0.06, 0.002, 0.13, mat('#4a6a8a', { emissive: '#2a4a6a', emissiveIntensity: 0.6 }), 0, 0.013, 0),
    );
  } else if (name.includes('remote')) {
    g.add(box(0.05, 0.022, 0.17, mat('#1b1b1b'), 0, 0.011, 0));
    g.add(box(0.016, 0.006, 0.016, mat('#d62828'), 0, 0.024, 0.06));
  } else g.add(box(0.1, 0.06, 0.1, mat('#b8a68a'), 0, 0.03, 0));
  // Scaled up a bit: the real size would be invisible at third-person distance.
  g.scale.setScalar(1.8);
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** A sparkle so you can spot something small in the grass. */
export function glint(): THREE.Sprite {
  const s = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture(),
      color: '#fff6c0',
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }),
  );
  s.scale.setScalar(0.6);
  return s;
}

/** Heavy things the neighbors want moved (carried over your head, like the junk jobs). */
export function carryThing(name: string): THREE.Group {
  if (name.includes('cooler')) return cooler();
  const g = new THREE.Group();
  if (name.includes('chair')) {
    const fabric = mat('#2e3440', { rough: 0.8 });
    g.add(box(0.5, 0.1, 0.48, fabric, 0, 0.5, 0));
    g.add(box(0.46, 0.55, 0.08, fabric, 0, 0.85, -0.22).rotateX(-0.25));
    g.add(cyl(0.03, 0.03, 0.35, M.steel, 0, 0.28, 0, 8));
    for (let i = 0; i < 5; i++) {
      const leg = box(0.32, 0.04, 0.05, M.black, 0, 0.08, 0);
      leg.rotation.y = (i / 5) * Math.PI * 2;
      leg.translateX(0.15);
      g.add(leg);
    }
  } else if (name.includes('plank')) {
    g.add(box(2.2, 0.06, 0.3, mat('#9a7a4e', { rough: 0.9 }), 0, 0.03, 0));
  } else if (name.includes('groceries')) {
    const paper = mat('#b9915c', { rough: 0.9 });
    for (const [x, z] of [
      [-0.18, 0],
      [0.18, 0.05],
    ] as const) {
      g.add(box(0.3, 0.38, 0.22, paper, x, 0.19, z));
      g.add(sphere(0.1, mat('#4f8a3a'), x - 0.04, 0.4, z, 8));
      g.add(box(0.04, 0.16, 0.04, mat('#e8d27a'), x + 0.07, 0.42, z));
    }
  } else {
    // A cardboard box that says OILS on it in Sharpie.
    g.add(box(0.6, 0.42, 0.45, mat('#b5895a', { rough: 0.9 }), 0, 0.21, 0));
    g.add(box(0.61, 0.04, 0.12, mat('#d8c39a'), 0, 0.42, 0));
    g.add(box(0.3, 0.08, 0.005, mat('#1b1b1b'), 0, 0.25, 0.228));
  }
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** The hand-painted sign by your mailbox, and a bucket of suds. */
export function carWashSign(): THREE.Group {
  const g = signBoard(
    {
      w: 512,
      h: 320,
      bg: '#f4f1e6',
      fg: '#1d4e9e',
      border: '#1d4e9e',
      borderWidth: 10,
      lines: [
        { text: 'CAR WASH', size: 96, font: 'Anton' },
        { text: '$8 · HAND DONE', size: 44, font: 'Anton', color: '#c62828' },
        { text: 'soap extra', size: 34, font: 'Permanent Marker', color: '#333' },
      ],
      grime: true,
    },
    1.0,
    0.62,
    0.55,
    1,
  );
  const bucket = new THREE.Group();
  bucket.add(cyl(0.17, 0.14, 0.3, mat('#d8dde2', { metal: 0.4, rough: 0.4 }), 0, 0.15, 0, 14));
  bucket.add(
    cyl(0.155, 0.155, 0.02, mat('#cfe8f5', { emissive: '#6fa8c8', emissiveIntensity: 0.2 }), 0, 0.27, 0, 14),
  );
  bucket.add(box(0.14, 0.06, 0.09, mat('#f2c200', { rough: 1 }), 0.03, 0.3, 0.02));
  bucket.position.set(0.55, 0, 0.35);
  g.add(bucket);
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

/** A splatter of dried mud, stuck flat on a car panel (+z faces out). */
export function mudSplat(size: number, seed: number): THREE.Mesh {
  const shape = new THREE.Shape();
  const n = 9;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = size * (0.7 + 0.3 * Math.abs(Math.sin(seed * 3.7 + i * 1.9)));
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r * 0.75;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  const mud = mat('#7a5a34', { rough: 1, side: THREE.DoubleSide });
  const m = mesh(new THREE.ShapeGeometry(shape), mud, 0, 0, 0, { cast: false });
  // Flecks thrown off around it.
  for (let i = 0; i < 4; i++) {
    const a = seed * 2.3 + i * 1.7;
    const r = size * (1.15 + 0.35 * Math.abs(Math.sin(seed + i)));
    m.add(
      mesh(
        new THREE.CircleGeometry(size * (0.12 + 0.08 * (i % 2)), 7),
        mud,
        Math.cos(a) * r,
        Math.sin(a) * r * 0.8,
        0.001,
        {
          cast: false,
        },
      ),
    );
  }
  m.userData.noBatch = true;
  return m;
}
